import debounce from 'debounce-promise'
import moment from 'moment'
import { useContext, useEffect, useMemo, useRef } from 'react'

import { useDatesOrGuestsChanged } from './useDatesOrGuestsChanged'
import {
  DEFAULT_SNACKBAR_SAVE_ITEM_UPDATE_MESSAGE,
  useUpdateSavedItemBookmarkHandlers,
} from './useUpdateSavedItemSnackbarHandlers'

import { useOfferPageRate } from 'components/OfferPage/hooks/useOfferPageRate'
import { getDatesAndGuestsFromOfferPageState } from 'components/OfferPage/utils'
import { DAY_MONTH_NAME_SHORT } from 'constants/dateFormats'
import GeoContext from 'contexts/geoContext'
import { OfferPageState } from 'contexts/OfferPage/offerPageStateReducer'
import { useAppSelector } from 'hooks/reduxHooks'
import { formatOccupantsAsGuests } from 'lib/offer/occupancyUtils'
import { useUpdateTripItem } from '../api/tripItem'
import { useTrip } from '../api/trip'
import { getImmersiveTripId } from 'tripPlanner/selectors'
import {
  AccommodationItem,
  BookmarkDatesAndOccupancy,
  LETripItem,
  TripItem,
} from 'tripPlanner/types/tripItem'
import { createHomeAndVillasItemPayload } from 'tripPlanner/utils/itemConversions/bookmarks/homeAndVillas'

export const getHomeAndVillasUpdateSuccessSnackbarMessage = <
  T extends {
    checkIn?: string
    checkOut?: string
    occupancies?: Array<App.Occupants>
  },
>({
    checkIn,
    checkOut,
    occupancies,
  }: T): string => {
  const baseMessage = 'Details updated to '

  const dates =
    checkIn && checkOut ?
      `${moment(checkIn).format(DAY_MONTH_NAME_SHORT)} - ${moment(
          checkOut,
        ).format(DAY_MONTH_NAME_SHORT)}` :
      undefined
  const guests = occupancies ?
    formatOccupantsAsGuests(...occupancies) :
    undefined

  if (dates && guests) {
    return baseMessage + `${dates}, ${guests}`
  } else if (dates) {
    return baseMessage + dates
  } else if (guests) {
    return baseMessage + guests
  } else {
    return DEFAULT_SNACKBAR_SAVE_ITEM_UPDATE_MESSAGE
  }
}

export const useSyncHomeAndVillasTripItem = (
  state: OfferPageState,
  offer: App.Offer,
) => {
  const awaitingFilters = useRef(false)
  const attemptUpdates = useRef(false)
  const currentTripId = useAppSelector(getImmersiveTripId)
  const { currentCurrency, currentRegionCode } = useContext(GeoContext)
  const { data: currentTrip } = useTrip({ tripId: currentTripId })
  const { showUpdateErrorSnackbar, showUpdateSuccessSnackbar } =
    useUpdateSavedItemBookmarkHandlers()

  const homeAndVillasItemsRef = useRef<Array<AccommodationItem> | null>(null)
  homeAndVillasItemsRef.current = currentTrip?.items.filter(
    (item: TripItem): item is AccommodationItem =>
      item.type === 'ACCOMMODATION' &&
      !item.isBooked &&
      item.code === offer.id,
  ) ?? null

  const { mutate: updateMutate } = useUpdateTripItem({
    onSuccess: (item) => {
      // If the hotelItemRef exists, then we have a bookmarked item
      const savedHomeAndVillasItem = item as Extract<
        LETripItem,
        AccommodationItem
      >
      showUpdateSuccessSnackbar(
        getHomeAndVillasUpdateSuccessSnackbarMessage(
          savedHomeAndVillasItem.savedItemData,
        ),
        item.tripId,
        item.id,
      )
    },
    onError: showUpdateErrorSnackbar,
  })

  const datesAndGuests = getDatesAndGuestsFromOfferPageState(state)
  const datesOrGuestsChanged = useDatesOrGuestsChanged(datesAndGuests)
  const filtersApplied = !state.activeFilter
  const accommodationRateRef = useRef<
    ReturnType<typeof useOfferPageRate> | undefined
  >(undefined)
  const accommodationRate = useOfferPageRate(state)
  accommodationRateRef.current = accommodationRate
  const debouncedUpdate = useMemo(
    () =>
      debounce((datesAndGuests: BookmarkDatesAndOccupancy) => {
        const items = homeAndVillasItemsRef.current
        const hotelRate = accommodationRateRef.current
        if (!items || !hotelRate) return

        attemptUpdates.current = false

        items.forEach((item) => {
          const payload = createHomeAndVillasItemPayload({
            offer,
            datesAndGuests,
            currentRegionCode,
            currencyCode: currentCurrency,
            price: hotelRate.hotelPrice,
            value: hotelRate.hotelValue,
            roomTypeId: item.subCode,
          })
          updateMutate({
            tripId: item.tripId,
            tripItemId: item.id,
            tripItem: payload,
          })
        })
      }, 2000),
    [currentCurrency, currentRegionCode, offer, updateMutate],
  )

  useEffect(() => {
    if (datesOrGuestsChanged) {
      awaitingFilters.current = true
    }
    if (awaitingFilters.current && filtersApplied) {
      awaitingFilters.current = false
      attemptUpdates.current = true
    }
    if (attemptUpdates.current) {
      debouncedUpdate(datesAndGuests)
    }
  })
}
